import { useCallback } from 'react';

const checkGeom = (geom, coords) => {
  if (typeof geom.containsXY === 'function') return geom.containsXY(coords[0], coords[1]);
  if (geom.getPolygons) return geom.getPolygons().some(p => checkGeom(p, coords));
  return false;
};

const matchFeature = (features, coords) => {
  for (const f of features) {
    const geom = f.getGeometry();
    if (geom && checkGeom(geom, coords)) return f;
  }
  return null;
};

const matchAllFeatures = (features, coords) => {
  const result = [];
  for (const f of features) {
    const geom = f.getGeometry();
    if (geom && checkGeom(geom, coords)) result.push(f);
  }
  return result;
};

const featureToBarrio = (f) => ({
  id: f.get('id'),
  nombre: f.get('nombre'),
  tipo: f.get('tipo'),
});

export default function usePointInTarget(targetFeaturesRef, allBarriosFeaturesRef, onOutsideTargetConfirm) {
  const isInsideTarget = useCallback((coords) => {
    try {
      return targetFeaturesRef.current.some(tf => {
        const geom = tf.getGeometry();
        return geom && checkGeom(geom, coords);
      });
    } catch (e) {
      console.warn('Error checking point vs target:', e);
      return false;
    }
  }, [targetFeaturesRef]);

  const findTargetBarrio = useCallback((coords) => {
    try {
      const feature = matchFeature(targetFeaturesRef.current, coords);
      if (!feature) return null;
      return featureToBarrio(feature);
    } catch (e) {
      console.warn('Error finding target barrio:', e);
      return null;
    }
  }, [targetFeaturesRef]);

  const findBarrio = useCallback((coords) => {
    try {
      const found = matchFeature(allBarriosFeaturesRef.current, coords);
      if (!found) return null;
      return featureToBarrio(found);
    } catch (e) {
      console.warn('Error finding barrio for point:', e);
      return null;
    }
  }, [allBarriosFeaturesRef]);

  const findAllBarrios = useCallback((coords) => {
    try {
      const features = matchAllFeatures(allBarriosFeaturesRef.current, coords);
      return features.map(featureToBarrio);
    } catch (e) {
      console.warn('Error finding all barrios for point:', e);
      return [];
    }
  }, [allBarriosFeaturesRef]);

  const confirmIfOutside = useCallback((coords) => {
    const targetBarrio = findTargetBarrio(coords);
    if (targetBarrio) {
      return { confirmed: true, barrio: targetBarrio, isOutside: false };
    }

    const barrios = findAllBarrios(coords);
    let confirmed = false;
    let result = { confirmed: false, barrio: null, isOutside: true };
    if (barrios.length > 0) {
      const lista = barrios.map(b => `  • ${b.nombre} (${b.tipo})`).join('\n');
      confirmed = window.confirm(
        `El punto está fuera de los barrios seleccionados.\nSe encuentra en:\n${lista}\n\n¿Desea confirmar la ubicación y agregarlos como nuevos barrios seleccionados?`
      );
      if (confirmed && onOutsideTargetConfirm) {
        barrios.forEach(b => onOutsideTargetConfirm(b));
        const msg = barrios.map(b => `  • ${b.nombre} (${b.tipo})`).join('\n');
        alert(`Se agregaron los siguientes barrios como seleccionados:\n${msg}`);
        result = { confirmed: true, barrio: barrios[0], isOutside: true };
      } else if (confirmed) {
        result = { confirmed: true, barrio: barrios[0], isOutside: true };
      }
    } else {
      confirmed = window.confirm(
        'El punto no está dentro de ninguno de los barrios seleccionados.\nSe registrará como barrio "Otro" (se intentará obtener el nombre desde OpenStreetMap).\n¿Desea confirmar la ubicación?'
      );
      if (confirmed) {
        result = { confirmed: true, barrio: null, isOutside: true };
      }
    }
    return result;
  }, [findTargetBarrio, findAllBarrios, onOutsideTargetConfirm]);

  return { isInsideTarget, findTargetBarrio, findBarrio, findAllBarrios, confirmIfOutside };
}
